/**
 * Customer Loyalty Tier Component
 * Handles dynamic integration with Yotpo loyalty program data
 */


class CustomerLoyaltyTier {
  constructor() {
    this.container = document.querySelector('.customer-loyalty-tier');
    this.progressBar = document.querySelector('.progress-bar__fill');
    this.customerName = document.querySelector('.customer-loyalty-tier__name');
    this.currentTier = document.querySelector('.customer-loyalty-tier__current');
    this.nextTierInfo = document.querySelector('.customer-loyalty-tier__next');
    this.tierImage = document.querySelector('.customer-loyalty-tier__badge');
    
    this.yotpoLoaded = false;
    this.retryCount = 0;
    this.maxRetries = 10;
    
    this.init();
  }

  init() {
    // Wait for Yotpo to load
    this.waitForYotpo();
    
    // Set up progress bar animation
    this.animateProgressBar();
    
    // Listen for customer data updates
    this.setupEventListeners();
  }

  waitForYotpo() {
    if (window.yotpo || window.swell) {
      this.yotpoLoaded = true;
      this.loadCustomerData();
    } else if (this.retryCount < this.maxRetries) {
      this.retryCount++;
      setTimeout(() => this.waitForYotpo(), 500);
    } else {
      console.log('Yotpo not loaded, using fallback data');
      this.loadFallbackData();
    }
  }

  async loadCustomerData() {
    try {
      // Try Yotpo Loyalty first
      if (window.yotpo && window.yotpo.loyalty) {
        await this.loadYotpoData();
      }
      // Try Swell (alternative loyalty platform)
      else if (window.swell) {
        await this.loadSwellData();
      }
      // Fallback to Shopify customer metafields
      else {
        this.loadShopifyMetafields();
      }
    } catch (error) {
      console.error('Error loading customer loyalty data:', error);
      this.loadFallbackData();
    }
  }

  async loadYotpoData() {
    if (!window.yotpo.loyalty.customer) return;

    const customer = window.yotpo.loyalty.customer;
    const tierData = customer.tier_and_points;
    
    if (tierData) {
      // Get all available tiers to determine if this is the highest
      const allTiers = window.yotpo.loyalty.tiers || [];
      const currentTierIndex = allTiers.findIndex(tier => tier.name === tierData.tier?.name);
      const isHighestTier = currentTierIndex === allTiers.length - 1 || !tierData.next_tier;
      
      this.updateDisplay({
        customerName: customer.display_name || customer.email,
        currentTier: tierData.tier?.name || 'Member',
        nextTier: isHighestTier ? null : tierData.next_tier?.name,
        pointsToNext: isHighestTier ? 0 : tierData.points_to_next_tier,
        amountToNext: isHighestTier ? 0 : tierData.amount_to_next_tier,
        progress: isHighestTier ? 100 : this.calculateProgress(tierData),
        tierImage: tierData.tier?.image_url,
        isHighestTier: isHighestTier,
        allTiers: allTiers
      });
    }
  }

  async loadSwellData() {
    if (!window.swell || !window.swell.account) return;

    try {
      const account = await window.swell.account.get();
      const loyalty = account.loyalty;
      
      if (loyalty) {
        const isHighestTier = !loyalty.next_tier || loyalty.points_to_next_tier === 0;
        
        this.updateDisplay({
          customerName: account.name || account.email,
          currentTier: loyalty.tier?.name || 'Member',
          nextTier: isHighestTier ? null : loyalty.next_tier?.name,
          pointsToNext: isHighestTier ? 0 : loyalty.points_to_next_tier,
          amountToNext: isHighestTier ? 0 : loyalty.amount_to_next_tier,
          progress: isHighestTier ? 100 : this.calculateSwellProgress(loyalty),
          tierImage: loyalty.tier?.image?.file?.url,
          isHighestTier: isHighestTier
        });
      }
    } catch (error) {
      console.error('Error loading Swell data:', error);
    }
  }

  loadShopifyMetafields() {
    // This would typically be populated server-side in the Liquid template
    // But we can also fetch via AJAX if needed
    const customerData = this.getCustomerDataFromDOM();
    if (customerData) {
      this.updateDisplay(customerData);
    } else {
      // Try tag fallback if no DOM data available
      this.loadTagFallback();
    }
  }

  loadTagFallback() {
    // Check if customer has swell_vip tags
    const customerTags = this.getCustomerTagsFromDOM();
    const swellTag = customerTags.find(tag => tag.includes('swell_vip_'));
    
    if (swellTag) {
      const tierName = swellTag.replace('swell_vip_', '').toLowerCase();
      const tierData = this.getTierDataFromTag(tierName);
      
      if (tierData) {
        this.updateDisplay({
          ...tierData,
          usingTagFallback: true
        });
      }
    }
  }

  getTierDataFromTag(tierName) {
    const tiers = {
      'ground floor': { 
        currentTier: 'Ground Floor', 
        nextTier: 'The Lift', 
        tierIndex: 0,
        badge: 'BASE TIER'
      },
      'the lift': { 
        currentTier: 'The Lift', 
        nextTier: 'The Lounge', 
        tierIndex: 1,
        badge: ''
      },
      'the lounge': { 
        currentTier: 'The Lounge', 
        nextTier: 'The Penthouse', 
        tierIndex: 2,
        badge: ''
      },
      'the penthouse': { 
        currentTier: 'The Penthouse', 
        nextTier: "The Member's Club", 
        tierIndex: 3,
        badge: ''
      }
    };

    return tiers[tierName] || null;
  }

  getCustomerTagsFromDOM() {
    // Try to get tags from a data attribute or hidden element
    const tagsElement = document.querySelector('[data-customer-tags]');
    if (tagsElement) {
      return tagsElement.dataset.customerTags.split(',').map(tag => tag.trim());
    }
    return [];
  }

  // Calculate tier information from points balance
  calculateTierFromPoints(points) {
    const tiers = [
      { name: 'Ground Floor', min: 0, max: 150, badge: 'BASE TIER' },
      { name: 'The Lift', min: 151, max: 350, badge: '' },
      { name: 'The Lounge', min: 351, max: 750, badge: '' },
      { name: 'The Penthouse', min: 751, max: 14999, badge: '' },
      { name: "The Member's Club", min: 15000, max: 999999, badge: 'LOCKED' }
    ];

    let currentTier = tiers[0];
    let nextTier = tiers[1];
    let tierIndex = 0;

    for (let i = 0; i < tiers.length; i++) {
      if (points >= tiers[i].min && points <= tiers[i].max) {
        currentTier = tiers[i];
        nextTier = tiers[i + 1] || null;
        tierIndex = i;
        break;
      }
    }

    const isHighestTier = !nextTier;
    const amountToNext = nextTier ? nextTier.min - points : 0;
    const progress = isHighestTier ? 100 : 
      Math.round(((points - currentTier.min) / (currentTier.max - currentTier.min)) * 100);

    return {
      currentTier: currentTier.name,
      currentTierBadge: currentTier.badge,
      nextTier: nextTier?.name,
      amountToNext: Math.max(0, amountToNext),
      progress: Math.max(0, Math.min(100, progress)),
      isHighestTier,
      tierIndex
    };
  }

  loadFallbackData() {
    // Use data from section settings as fallback
    const fallbackData = {
      customerName: 'Valued Customer',
      currentTier: 'THE LIFT',
      nextTier: 'THE LOUNGE',
      amountToNext: 140,
      progress: 25
    };
    
    this.updateDisplay(fallbackData);
  }

  getCustomerDataFromDOM() {
    // Extract data that was rendered server-side
    const container = this.container;
    if (!container) return null;

    return {
      customerName: this.customerName?.textContent?.trim(),
      currentTier: this.extractTierFromText(this.currentTier?.textContent),
      nextTier: this.extractNextTierFromText(this.nextTierInfo?.textContent),
      amountToNext: this.extractAmountFromText(this.nextTierInfo?.textContent),
      progress: this.progressBar?.dataset?.progress ? parseFloat(this.progressBar.dataset.progress) : 0
    };
  }

  updateDisplay(data) {
    if (data.customerName && this.customerName) {
      this.customerName.textContent = data.customerName.toUpperCase();
    }

    if (data.currentTier && this.currentTier) {
      this.currentTier.textContent = `YOU'RE IN ${data.currentTier.toUpperCase()}`;
    }

    if (data.nextTier && this.nextTierInfo) {
      if (data.usingTagFallback) {
        // Show simplified message for tag fallback
        this.nextTierInfo.innerHTML = `
          Next level: <strong>${data.nextTier.toUpperCase()}</strong>
        `;
      } else if (data.amountToNext) {
        // Show spend amount for points-based system
        const currency = this.getCurrency();
        this.nextTierInfo.innerHTML = `
          Spend <strong>${currency}${data.amountToNext}</strong> to unlock <strong>${data.nextTier.toUpperCase()}</strong> access
        `;
      }
    } else if (!data.nextTier && data.currentTier && this.nextTierInfo) {
      // Show highest tier message with current tier name
      this.nextTierInfo.innerHTML = `
        🎉 You're at the highest tier: <strong>${data.currentTier.toUpperCase()}</strong>!
      `;
      this.nextTierInfo.classList.add('customer-loyalty-tier__max');
    }

    if (data.progress !== undefined && this.progressBar) {
      this.progressBar.style.width = `${Math.min(data.progress, 100)}%`;
      this.progressBar.dataset.progress = data.progress;
    }

    if (data.tierImage && this.tierImage) {
      this.tierImage.src = data.tierImage;
      this.tierImage.alt = `${data.currentTier} tier badge`;
    }

    // Trigger custom event for other components
    this.dispatchUpdateEvent(data);
  }

  calculateProgress(tierData) {
    if (!tierData.points_to_next_tier || !tierData.tier_min_points) return 0;
    
    const currentPoints = tierData.points_earned || 0;
    const tierMinPoints = tierData.tier_min_points || 0;
    const nextTierPoints = tierMinPoints + tierData.points_to_next_tier;
    
    return Math.round(((currentPoints - tierMinPoints) / (nextTierPoints - tierMinPoints)) * 100);
  }

  calculateSwellProgress(loyalty) {
    if (!loyalty.points_to_next_tier || !loyalty.points_earned) return 0;
    
    const totalPointsNeeded = loyalty.points_earned + loyalty.points_to_next_tier;
    return Math.round((loyalty.points_earned / totalPointsNeeded) * 100);
  }

  animateProgressBar() {
    if (!this.progressBar) return;

    const targetWidth = this.progressBar.dataset.progress || 0;
    this.progressBar.style.width = '0%';
    
    // Animate to target width
    setTimeout(() => {
      this.progressBar.style.width = `${targetWidth}%`;
    }, 300);
  }

  setupEventListeners() {
    // Listen for Yotpo events
    document.addEventListener('yotpo:loyalty:loaded', () => {
      this.loadCustomerData();
    });

    // Listen for Swell events
    document.addEventListener('swell:account:loaded', () => {
      this.loadCustomerData();
    });

    // Listen for customer login/logout
    document.addEventListener('customer:login', () => {
      setTimeout(() => this.loadCustomerData(), 1000);
    });

    document.addEventListener('customer:logout', () => {
      this.loadFallbackData();
    });
  }

  dispatchUpdateEvent(data) {
    const event = new CustomEvent('loyalty:tier:updated', {
      detail: data,
      bubbles: true
    });
    
    if (this.container) {
      this.container.dispatchEvent(event);
    }
  }

  // Utility methods
  extractTierFromText(text) {
    if (!text) return null;
    const match = text.match(/YOU'RE IN (.+)/);
    return match ? match[1].trim() : null;
  }

  extractNextTierFromText(text) {
    if (!text) return null;
    const match = text.match(/unlock (.+) access/);
    return match ? match[1].trim() : null;
  }

  extractAmountFromText(text) {
    if (!text) return null;
    const match = text.match(/\$(\d+(?:\.\d{2})?)/);
    return match ? parseFloat(match[1]) : null;
  }

  getCurrency() {
    // Try to get currency from Shopify
    if (window.Shopify && window.Shopify.currency) {
      return window.Shopify.currency.active === 'USD' ? '$' : window.Shopify.currency.active;
    }
    return '$'; // Default fallback
  }

  // Public methods for external use
  refresh() {
    this.loadCustomerData();
  }

  updateProgress(newProgress) {
    if (this.progressBar) {
      this.progressBar.style.width = `${Math.min(newProgress, 100)}%`;
      this.progressBar.dataset.progress = newProgress;
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.customer-loyalty-tier')) {
    window.customerLoyaltyTier = new CustomerLoyaltyTier();
  }
});

// Export for module use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomerLoyaltyTier;
}
